class WebSocketManager {
  constructor(url, options = {}) {
    this.url = url
    this.ws = null

    this.reconnectInterval = options.reconnectInterval || 5000
    this.maxReconnectInterval = options.maxReconnectInterval || 30000
    // Binance 不需要心跳，但需要检测连接是否存活
    this.connectionCheckInterval = options.connectionCheckInterval || 30000

    this.isManualClose = false
    this.reconnectTimer = null
    this.connectionCheckTimer = null
    this.reconnectAttempts = 0
    this.lastMessageTime = Date.now()  // 记录最后收到消息的时间
    
    this.eventHandlers = {
      onMessage: null,
      onOpen: null,
      onClose: null,
      onError: null,
      onReconnect: null,
    }
  }

  // 连接 WebSocket
  connect() {
    // 清理之前的连接
    if (this.ws) {
      try {
        this.ws.onclose = null
        this.ws.close()
      } catch (e) {
        console.error("关闭旧连接失败:", e)
      }
      this.ws = null
    }

    console.log(`[Binance WebSocket] 尝试连接 (第${this.reconnectAttempts + 1}次)`)
    
    this.isManualClose = false
    
    try {
      this.ws = new WebSocket(this.url)
      
      const connectionTimeout = setTimeout(() => {
        if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
          console.warn("[Binance WebSocket] 连接超时")
          this.ws.close()
        }
      }, 10000)

      this.ws.onopen = (event) => {
        clearTimeout(connectionTimeout)
        console.log(`[Binance WebSocket] 连接成功`)
        this.reconnectAttempts = 0
        this.lastMessageTime = Date.now()
        
        // 启动连接健康检查（替代心跳）
        this.startConnectionCheck()
        
        this.eventHandlers.onOpen && this.eventHandlers.onOpen(event)
      }

      this.ws.onmessage = (event) => {
        try {
          // 更新最后消息时间
          this.lastMessageTime = Date.now()
          
          const data = event.data
          
          // Binance 会发送 ping 消息（文本格式）
          if (data === "ping") {
            console.log("[Binance WebSocket] 收到 ping，发送 pong")
            this.ws.send("pong")
            return
          }
          
          // 处理 binance 的错误响应
          if (typeof data === 'string') {
            try {
              const parsed = JSON.parse(data)
              if (parsed.error) {
                console.error("[Binance WebSocket] 收到错误:", parsed.error)
                // 某些错误需要重新连接
                if (parsed.error.code === 2) {
                  console.warn("收到无效请求错误，可能是发送了不支持的格式")
                }
              }
            } catch (e) {
              // 不是 JSON，继续处理
            }
          }
          
          // 正常消息处理
          this.eventHandlers.onMessage && this.eventHandlers.onMessage(data)
          
        } catch (error) {
          console.error("[Binance WebSocket] 解析消息失败:", error, event.data)
        }
      }

      this.ws.onclose = (event) => {
        clearTimeout(connectionTimeout)
        console.log(`[Binance WebSocket] 连接关闭: code=${event.code}, reason=${event.reason || '无'}`)
        this.stopConnectionCheck()
        this.eventHandlers.onClose && this.eventHandlers.onClose(event)

        if (!this.isManualClose) {
          this.reconnect()
        }
      }

      this.ws.onerror = (error) => {
        console.error(`[Binance WebSocket] 错误:`, error)
        this.eventHandlers.onError && this.eventHandlers.onError(error)
      }
      
    } catch (error) {
      console.error("[Binance WebSocket] 创建连接失败:", error)
      if (!this.isManualClose) {
        this.reconnect()
      }
    }
  }

  // 启动连接健康检查（检测是否还在接收数据）
  startConnectionCheck() {
    this.stopConnectionCheck()
    
    this.connectionCheckTimer = setInterval(() => {
      const now = Date.now()
      const timeSinceLastMessage = now - this.lastMessageTime
      
      // 如果超过 1 分钟没有收到任何消息，认为连接可能有问题
      if (timeSinceLastMessage > 60000) {
        console.warn(`[Binance WebSocket] 超过 1 分钟未收到数据，最后消息时间: ${new Date(this.lastMessageTime).toLocaleTimeString()}`)
        
        // 检查连接状态
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          console.warn("[Binance WebSocket] 连接疑似僵死，主动关闭重连")
          // 主动关闭会触发 onclose，进而重连
          this.ws.close(4000, "Connection stale")
        }
      } else if (timeSinceLastMessage > 30000) {
        // 30秒没收到数据，发出警告
        console.debug(`[Binance WebSocket] 已 ${Math.floor(timeSinceLastMessage/1000)} 秒未收到数据`)
      }
    }, this.connectionCheckInterval)
  }

  // 停止连接检查
  stopConnectionCheck() {
    if (this.connectionCheckTimer) {
      clearInterval(this.connectionCheckTimer)
      this.connectionCheckTimer = null
    }
  }

  // 发送数据（仅用于需要主动发送的场景，Binance 的订阅流通常不需要）
  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const message = typeof data === 'string' ? data : JSON.stringify(data)
      this.ws.send(message)
      console.log("[Binance WebSocket] 发送:", message)
      return true
    }
    console.warn("[Binance WebSocket] 连接未就绪，无法发送")
    return false
  }

  // 订阅特定的数据流（如果需要）
  subscribe(params) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const subscribeMsg = JSON.stringify({
        method: "SUBSCRIBE",
        params: params,
        id: Date.now()
      })
      this.ws.send(subscribeMsg)
      console.log("[Binance WebSocket] 订阅:", params)
    }
  }

  // 取消订阅
  unsubscribe(params) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const unsubscribeMsg = JSON.stringify({
        method: "UNSUBSCRIBE",
        params: params,
        id: Date.now()
      })
      this.ws.send(unsubscribeMsg)
      console.log("[Binance WebSocket] 取消订阅:", params)
    }
  }

  // 重连（指数退避）
  reconnect() {
    if (this.reconnectTimer) {
      return
    }
    
    // 限制最大重连次数，避免无限重连
    if (this.reconnectAttempts >= 20) {
      console.error("[Binance WebSocket] 达到最大重连次数，停止重连")
      return
    }
    
    // 指数退避，但增加随机因素避免多个客户端同时重连
    const delay = Math.min(
      this.reconnectInterval * Math.pow(1.5, this.reconnectAttempts),
      this.maxReconnectInterval
    ) + Math.random() * 1000
    
    console.log(`[Binance WebSocket] ${Math.round(delay)}ms 后尝试重连 (第${this.reconnectAttempts + 1}次)`)
    
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.reconnectAttempts++
      this.eventHandlers.onReconnect && this.eventHandlers.onReconnect(this.reconnectAttempts)
      this.connect()
    }, delay)
  }

  // 注册事件监听
  on(event, handler) {
    if (this.eventHandlers.hasOwnProperty(event)) {
      this.eventHandlers[event] = handler
    } else {
      console.warn(`[Binance WebSocket] 未知事件: ${event}`)
    }
  }

  // 检查连接状态
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN
  }

  // 手动关闭
  close() {
    console.log("[Binance WebSocket] 手动关闭连接")
    this.isManualClose = true
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    this.stopConnectionCheck()
    
    if (this.ws) {
      this.ws.onclose = null
      this.ws.close()
      this.ws = null
    }
    
    this.reconnectAttempts = 0
  }
}

export default WebSocketManager